import * as React from 'react'
import { useSearchParams } from 'react-router-dom'
import { Clock3, KeyRound, Settings, ShieldCheck, UserCircle2 } from 'lucide-react'
import { Avatar } from '@/components/common/Avatar'
import { Badge, Card, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui'
import { PageShell } from '@/components/common/PageShell'
import { ChangePasswordForm } from '@/features/auth/components/ChangePasswordForm'
import { EditMyProfileForm } from '@/features/settings/components/EditMyProfileForm'
import { SessionsList } from '@/features/settings/components/SessionsList'
import { useMe } from '@/features/settings/hooks/useMe'
import { formatDate } from '@/lib/formatters'
import { QueryErrorState } from '@/components/common/QueryErrorState'

const SETTINGS_BREADCRUMB = [] as const
const SETTINGS_DESCRIPTION =
  'Account details, password, and where you are signed in — open the section you need.'

type SettingsTabId = 'account' | 'security' | 'sessions'

const SETTINGS_TAB_QUERY = 'tab'

function tabFromSearchParam(raw: string | null): SettingsTabId {
  if (raw === 'security' || raw === 'sessions') return raw
  return 'account'
}

function InfoTile({
  label,
  value,
  mono = false,
}: {
  label: string
  value: string
  mono?: boolean
}): React.ReactElement {
  return (
    <div className="rounded-2xl border border-gray-200/80 bg-gray-50/80 p-4 dark:border-gray-700/80 dark:bg-gray-900/30">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <p className={`mt-2 text-sm text-gray-900 dark:text-gray-100 ${mono ? 'font-mono' : ''}`}>
        {value}
      </p>
    </div>
  )
}

export function SettingsPage(): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: me, isLoading, error, refetch } = useMe()

  const activeTab: SettingsTabId = tabFromSearchParam(searchParams.get(SETTINGS_TAB_QUERY))

  const handleTabChange = React.useCallback(
    (value: string) => {
      const next = tabFromSearchParam(value)
      if (next === 'account') {
        setSearchParams({}, { replace: true })
      } else {
        setSearchParams({ [SETTINGS_TAB_QUERY]: next }, { replace: true })
      }
    },
    [setSearchParams]
  )

  if (isLoading) {
    return (
      <PageShell
        breadcrumbItems={[...SETTINGS_BREADCRUMB]}
        icon={Settings}
        title="Settings"
        description={SETTINGS_DESCRIPTION}
        className="gap-0"
        hideHeader
      >
        <div className="space-y-4">
          <div className="h-64 animate-pulse rounded-3xl bg-gray-100 dark:bg-gray-800/70" />
          <div className="h-14 animate-pulse rounded-2xl bg-gray-100 dark:bg-gray-800/70" />
          <div className="h-60 animate-pulse rounded-3xl bg-gray-100 dark:bg-gray-800/70" />
        </div>
      </PageShell>
    )
  }

  if (error) {
    return (
      <PageShell
        breadcrumbItems={[...SETTINGS_BREADCRUMB]}
        icon={Settings}
        title="Settings"
        description={SETTINGS_DESCRIPTION}
        className="gap-0"
        hideHeader
      >
        <QueryErrorState
          error={error}
          title="Couldn't load your profile"
          onRetry={refetch}
        />
      </PageShell>
    )
  }

  if (!me) {
    return (
      <PageShell
        breadcrumbItems={[...SETTINGS_BREADCRUMB]}
        icon={Settings}
        title="Settings"
        description={SETTINGS_DESCRIPTION}
        className="gap-0"
        hideHeader
      >
        <p className="text-sm text-gray-600 dark:text-gray-400">No profile data available.</p>
      </PageShell>
    )
  }

  const fullName = [me.firstName, me.lastName].filter(Boolean).join(' ') || '—'
  const profileName = fullName
  const employmentValue =
    [me.employment?.employmentType, me.employment?.workLocation].filter(Boolean).join(' · ') || '—'
  const compensationValue = `${[me.compensation?.currency, me.compensation?.payFrequency]
    .filter(Boolean)
    .join(', ') || '—'}${me.compensation?.taxInfo?.taxExempt ? ' · Tax exempt' : ''}`

  return (
    <PageShell
      breadcrumbItems={[...SETTINGS_BREADCRUMB]}
      icon={Settings}
      title="Settings"
      description={SETTINGS_DESCRIPTION}
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <Card className="overflow-hidden border-primary/15 bg-gradient-to-br from-primary/10 via-white to-white shadow-sm dark:from-primary/10 dark:via-gray-800 dark:to-gray-900">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
            <div className="flex flex-col gap-6 sm:flex-row">
              <div className="flex shrink-0 flex-col items-center gap-3 sm:w-28">
                <Avatar
                  name={profileName === '—' ? me.email : profileName}
                  src={me.avatar || undefined}
                  size="lg"
                  className="h-20 w-20 rounded-3xl text-2xl shadow-sm"
                />
                <Badge variant={me.status === 'active' ? 'success' : 'neutral'} className="capitalize">
                  {me.status}
                </Badge>
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                    <UserCircle2 className="h-3.5 w-3.5" />
                    Personal profile
                  </span>
                </div>
                <h2 className="mt-4 text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                  {profileName}
                </h2>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{me.email}</p>

                <div className="mt-6 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl border border-white/60 bg-white/90 p-4 dark:border-gray-700/80 dark:bg-gray-900/40">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                      Employee ID
                    </p>
                    <p className="mt-2 font-mono text-sm text-gray-900 dark:text-gray-100">
                      {me.employeeId || '—'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-white/60 bg-white/90 p-4 dark:border-gray-700/80 dark:bg-gray-900/40">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                      Employment
                    </p>
                    <p className="mt-2 text-sm capitalize text-gray-900 dark:text-gray-100">
                      {employmentValue}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-white/60 bg-white/90 p-4 dark:border-gray-700/80 dark:bg-gray-900/40">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                      Compensation
                    </p>
                    <p className="mt-2 text-sm text-gray-900 dark:text-gray-100">
                      {compensationValue}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
              <div className="rounded-2xl border border-primary/10 bg-white/85 p-4 dark:border-primary/20 dark:bg-gray-900/40">
                <div className="flex items-center gap-3">
                  <Clock3 className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Last login
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {me.lastLoginAt ? formatDate(me.lastLoginAt, 'PPpp') : 'No login recorded'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl border border-gray-200/80 bg-gray-50/80 p-4 dark:border-gray-700/80 dark:bg-gray-900/30">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Password hygiene
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {me.lastPasswordChange
                        ? `Updated ${formatDate(me.lastPasswordChange, 'PP')}`
                        : 'No recent password update recorded'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList
            aria-label="Settings sections"
            className="w-full gap-2 overflow-x-auto rounded-2xl border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-800"
          >
            <TabsTrigger value="account" className="flex-1 rounded-xl border-b-0 px-4 py-3">
              Account
            </TabsTrigger>
            <TabsTrigger value="security" className="flex-1 rounded-xl border-b-0 px-4 py-3">
              Security
            </TabsTrigger>
            <TabsTrigger value="sessions" className="flex-1 rounded-xl border-b-0 px-4 py-3">
              Sessions
            </TabsTrigger>
          </TabsList>

          <TabsContent value="account" className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Profile settings</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Update the personal details you control, with organization-managed account details kept separate below.
              </p>
            </div>

            <EditMyProfileForm me={me} />

            <div className="grid gap-6 xl:grid-cols-2">
              <Card
                className="border-gray-200/80 shadow-sm dark:border-gray-700/80"
                header={
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                      Account details
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Read-only information managed by your organization.
                    </p>
                  </div>
                }
              >
                <div className="grid gap-3">
                  <InfoTile label="Full name" value={fullName} />
                  <InfoTile label="Email" value={me.email} />
                  <InfoTile label="Employee ID" value={me.employeeId || '—'} mono />
                  <InfoTile label="Employment" value={employmentValue} />
                  <InfoTile label="Status" value={me.status} />
                  <InfoTile label="Last login" value={me.lastLoginAt ? formatDate(me.lastLoginAt, 'PPpp') : '—'} />
                </div>
              </Card>

              <Card
                className="border-gray-200/80 shadow-sm dark:border-gray-700/80"
                header={
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                      Compensation
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Payroll-facing information visible on your account.
                    </p>
                  </div>
                }
              >
                <div className="grid gap-3">
                  <InfoTile
                    label="Currency and cycle"
                    value={
                      [me.compensation?.currency, me.compensation?.payFrequency].filter(Boolean).join(', ') || '—'
                    }
                  />
                  <InfoTile
                    label="Tax status"
                    value={me.compensation?.taxInfo?.taxExempt ? 'Tax exempt' : 'Standard tax handling'}
                  />
                  <InfoTile
                    label="Password changed"
                    value={me.lastPasswordChange ? formatDate(me.lastPasswordChange, 'PP') : '—'}
                  />
                </div>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="security" className="space-y-6">
            <Card className="border-gray-200/80 shadow-sm dark:border-gray-700/80">
              <div className="grid gap-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
                <div className="rounded-3xl border border-primary/10 bg-gradient-to-br from-primary/10 via-white to-white p-6 dark:border-primary/20 dark:from-primary/10 dark:via-gray-800 dark:to-gray-900">
                  <div className="flex items-center gap-3">
                    <KeyRound className="h-5 w-5 text-primary" />
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Password security
                    </h2>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-400">
                    Update your password to keep your account secure. Use a unique password that you do not reuse elsewhere.
                  </p>
                  {me.lastPasswordChange && (
                    <div className="mt-6 rounded-2xl border border-white/60 bg-white/85 p-4 dark:border-gray-700/80 dark:bg-gray-900/40">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                        Last changed
                      </p>
                      <p className="mt-2 text-sm text-gray-900 dark:text-gray-100">
                        {formatDate(me.lastPasswordChange, 'PP')}
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex justify-center lg:justify-end">
                  <ChangePasswordForm />
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="sessions" className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Active sessions</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Devices and browsers where you are signed in. Sign out of any you don&apos;t recognize.
              </p>
            </div>
            <SessionsList />
          </TabsContent>
        </Tabs>
      </div>
    </PageShell>
  )
}

SettingsPage.displayName = 'SettingsPage'
